import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { CatalogImportTab } from '@/pages/catalogImport/store';
import type { ImportRow, ImportRowExclusionHandlers } from '../../importRows';
import { ExcludedRowsBar } from '../excludedRows';
import { ImportCardList } from '../importCards';
import { ImportTable } from '../importTable';

/** What an empty table says: the file has no rows, or the tab has none. */
const EMPTY_KEY = {
    all: 'catalogImport.products.table.empty',
    needsAttention: 'catalogImport.products.table.emptyTab.needsAttention',
    newProducts: 'catalogImport.products.table.emptyTab.newProducts',
    restock: 'catalogImport.products.table.emptyTab.restock',
    excluded: 'catalogImport.products.table.emptyTab.excluded',
} as const satisfies Record<CatalogImportTab, string>;

type ImportTabRowsProps = ImportRowExclusionHandlers & {
    tab: CatalogImportTab;
    /** The tab's rows, in file order. */
    rows: ImportRow[];
    /** Every row of the file, for the footer's "of m". */
    totalRows: number;
    /** The rows the user excluded: the Excluded tab's count. */
    excludedCount: number;
    /** A phone: the rows as cards instead of the grid. */
    isNarrow: boolean;
    /** The import is running: no row opens and no exclusion changes. */
    isDisabled: boolean;
    /** A row's press: opens it in the drawer. */
    onOpenRow: (rowNumber: number) => void;
    /** The Excluded tab's Include all back. */
    onIncludeAll: () => void;
};

/**
 * The selected tab's panel: on Excluded, what an excluded row means and
 * Include all back (`ExcludedRowsBar`) over the rows; then the rows, as the
 * grid (`ImportTable`) or, on a phone, as cards (`ImportCardList`), with the
 * tab's empty sentence.
 */
export const ImportTabRows: React.FC<ImportTabRowsProps> = ({
    tab,
    excludedCount,
    isNarrow,
    isDisabled,
    onIncludeAll,
    ...rowsProps
}) => {
    const { t } = useTranslation('pages');
    const rows = { ...rowsProps, emptyMessage: t(EMPTY_KEY[tab]), isDisabled };

    return (
        <>
            {tab === 'excluded' ? (
                <ExcludedRowsBar
                    excludedCount={excludedCount}
                    isDisabled={isDisabled}
                    onIncludeAll={onIncludeAll}
                />
            ) : null}
            {isNarrow ? (
                <ImportCardList {...rows} />
            ) : (
                <ImportTable {...rows} />
            )}
        </>
    );
};
